import { useEffect, useState, useCallback, useRef } from 'react';
import DataTable from '../../components/common/DataTable';
import DatePicker from '../../components/common/DatePicker';
import Select from '../../components/common/Select';
import { getWalletBalance, getWalletTransactions } from '../../services/walletService';
import { handleApiError } from '../../utils/handleApiError';
import api from '../../services/api';

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
});

const formatCurrency = (value) => {
  return currencyFormatter.format(toNumber(value));
};

const statusStyle = (type) => {
  const t = type?.toLowerCase();
  if (t === 'debit') return 'bg-red-50 text-red-600';
  if (t === 'credit') return 'bg-emerald-50 text-emerald-600';
  if (t === 'hold') return 'bg-yellow-100 text-yellow-700';
  return 'bg-gray-50 text-gray-600';
};

const columns = [
  {
    accessorKey: 'txn_id',
    header: 'Wallet Transaction ID',
    size: 200,
    Cell: ({ cell, row }) => (
      <span className="font-mono text-xs text-gray-500">
        {cell.getValue() || row.original.wt_id || '—'}
      </span>
    ),
  },
  {
    accessorKey: 'created_at',
    header: 'Date & Time',
    size: 140,
    Cell: ({ cell }) => {
      const d = new Date(cell.getValue());
      return (
        <div>
          <div className="text-[13px] text-gray-900">{d.toLocaleDateString()}</div>
          <div className="text-[11px] text-gray-400 mt-0.5">{d.toLocaleTimeString()}</div>
        </div>
      );
    },
  },
  {
    accessorKey: 'utr_number',
    header: 'UTR',
    Cell: ({ cell, row }) => (
      <span className="font-mono text-sm text-gray-500">
        {cell.getValue() || row.original.utr || '—'}
      </span>
    ),
  },
  {
    accessorKey: 'type',
    header: 'Type',
    size: 100,
    Cell: ({ cell }) => {
      const type = cell.getValue();
      return (
        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full uppercase ${statusStyle(type)}`}>
          {type || '—'}
        </span>
      );
    },
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 200,
    Cell: ({ cell }) => (
      <span className="text-[13px] font-medium text-gray-900">{cell.getValue() || '—'}</span>
    ),
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    size: 120,
    Cell: ({ row }) => {
      const amt = toNumber(row.original.amount || 0);
      const before = toNumber(row.original.balance_before || 0);
      const after = toNumber(row.original.balance_after || 0);
      const isIncrease = after > before;

      return (
        <div>
          <div className={`text-[13px] font-bold ${isIncrease ? "text-emerald-600" : "text-red-600"}`}>
            {isIncrease ? "+" : "-"}{formatCurrency(Math.abs(amt))}
          </div>
          <div className="mt-0.5 text-[10px] text-gray-400">
            Before: {formatCurrency(before)}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'balance_after',
    header: 'Closing Balance',
    size: 120,
    Cell: ({ cell }) => (
      <span className="text-[13px] font-bold text-gray-900">
        {formatCurrency(cell.getValue() || 0)}
      </span>
    ),
  }
];

export default function WalletDashboard() {
  const LIMIT = 10;
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    has_more: false,
    total: 0,
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loadingBal, setLoadingBal] = useState(true);
  const [loadingTxn, setLoadingTxn] = useState(true);
  
  // Filter states - Same as Dashboard
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateError, setDateError] = useState('');
  
  const [exporting, setExporting] = useState(false);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowFormatMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Normalize pagination
  const normalizePagination = (raw, fallbackPage, fallbackLimit, rowCount) => {
    const p = raw || {};
    const current = Number(p.current_page ?? p.page ?? p.currentPage ?? p.page_number) || fallbackPage;
    const total = Number(p.total ?? p.total_count ?? p.count) || 0;
    const lastByTotal = total > 0 ? Math.ceil(total / fallbackLimit) : 0;
    const last = Number(p.last_page ?? p.total_pages ?? p.lastPage ?? p.page_count) || lastByTotal || (rowCount === fallbackLimit ? current + 1 : current);
    return {
      current_page: current,
      last_page: Math.max(1, last),
      total,
      has_more: Boolean(p.has_more ?? current < Math.max(1, last)),
    };
  };

  // Validate dates - Same as Dashboard
  const validateDates = useCallback((from, to) => {
    if (from && to && from > to) {
      setDateError("From date cannot be after To date.");
    } else {
      setDateError("");
    }
  }, []);

  // Fetch wallet balance
  useEffect(() => {
    getWalletBalance()
      .then((res) => setWalletData(res.data?.data || null))
      .catch(handleApiError)
      .finally(() => setLoadingBal(false));
  }, []);

  // Debounced search - Same as Dashboard
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, fromDate, toDate, typeFilter]);

  // Fetch transactions - Same as Dashboard pattern
  useEffect(() => {
    setLoadingTxn(true);
    
    getWalletTransactions({
      page,
      limit,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      type: typeFilter || undefined,
      search: searchText || undefined,
    })
      .then((res) => {
        const d = res.data?.data;
        const rows = d?.transactions || [];
        const normalized = normalizePagination(
          d?.pagination || d?.meta || res.data?.pagination || res.data?.meta,
          page,
          limit,
          rows.length
        );
        setTransactions(rows);
        setPagination(normalized);
      })
      .catch(handleApiError)
      .finally(() => setLoadingTxn(false));
  }, [page, limit, searchText, fromDate, toDate, typeFilter]);

  // Handle export
  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (typeFilter) params.append('type', typeFilter);
      if (searchText) params.append('search', searchText);
      params.append('format', format);

      const res = await api.get(`/wallet/export?${params.toString()}`, { responseType: 'blob' });

      const contentType = res.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const json = JSON.parse(text);
        toast.info(json.message || 'Export queued. Check Reports section to download.');
        return;
      }

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      link.setAttribute('download', `wallet_export_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleLimitChange = (val) => {
    setLimit(val);
    setPage(1);
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-[#F8F9FA]">

      {/* Header */}
      <div className="py-6 pt-0">
        <h2 className="text-[26px] md:text-[34px] font-bold text-primary mb-2">
          Wallet Dashboard
        </h2>
        <p className="text-[14px] md:text-[15px] text-[#6B6471] leading-relaxed">
          Manage your liquid assets and view real-time transaction history.
        </p>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-[12px] shadow-[0px_20px_40px_0px_rgba(75,35,100,0.08)] flex items-center h-[100px]">
          <div className="w-[4px] h-full bg-primary-light rounded-l-[12px] shrink-0" />
          <div className="px-5 w-full">
            <p className="text-[10px] font-semibold text-[#4C444E] tracking-[1.5px] uppercase mb-1">Available Balance</p>
            <p className="text-[24px] md:text-[28px] font-bold text-primary">
              {loadingBal ? '...' : formatCurrency(walletData?.available_balance || 0)}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-[12px] shadow-[0px_20px_40px_0px_rgba(75,35,100,0.08)] flex items-center h-[100px]">
          <div className="w-[4px] h-full bg-primary-mid rounded-l-[12px] shrink-0" />
          <div className="px-5 w-full">
            <p className="text-[10px] font-semibold text-[#4C444E] tracking-[1.5px] uppercase mb-1">Total Balance</p>
            <p className="text-[24px] md:text-[28px] font-bold text-primary">
              {loadingBal ? '...' : formatCurrency(walletData?.total_balance || 0)}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-[12px] shadow-[0px_20px_40px_0px_rgba(75,35,100,0.08)] flex items-center h-[100px] sm:col-span-2 lg:col-span-1">
          <div className="w-[4px] h-full bg-amber-400 rounded-l-[12px] shrink-0" />
          <div className="px-5 w-full">
            <p className="text-[10px] font-semibold text-[#4C444E] tracking-[1.5px] uppercase mb-1">Hold Balance</p>
            <p className="text-[24px] md:text-[28px] font-bold text-yellow-500">
              {loadingBal ? '...' : formatCurrency(walletData?.hold_balance || 0)}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-[12px] shadow-[0px_20px_40px_0px_rgba(75,35,100,0.08)] flex items-center h-[100px]">
          <div className="w-[4px] h-full bg-primary-mid rounded-l-[12px] shrink-0" />
          <div className="px-5 w-full">
            <p className="text-[10px] font-semibold text-[#4C444E] tracking-[1.5px] uppercase mb-1">Unsettled Balance</p>
            <p className="text-[24px] md:text-[28px] font-bold text-primary">
              {loadingBal ? '...' : formatCurrency(walletData?.unsettled_balance || 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Filter Section - Same as Dashboard */}
      <div className="px-6 py-5 bg-[#F3F4F5] rounded-[16px] mb-3 border border-[#CEC3D01A]">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search by description..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="bg-white rounded-[12px] pl-8 pr-4 py-2.5 text-[13px] text-[#191C1D] border-none outline-none focus:ring-2 focus:ring-primary-mid/30 w-52 transition"
            />
          </div>

          {/* From Date */}
          <div className={`flex items-center gap-3 py-2 px-4 rounded-[12px] bg-white ring-1 transition-all ${dateError ? 'ring-red-400' : 'ring-transparent'}`}>
            <span className="text-[12px] font-semibold text-[#4C444E] uppercase">FROM</span>
            <DatePicker
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); validateDates(e.target.value, toDate); }}
              placeholder="dd/mm/yyyy"
              className="w-[110px]"
            />
          </div>

          {/* To Date */}
          <div className={`flex items-center gap-3 py-2 px-4 rounded-[12px] bg-white ring-1 transition-all ${dateError ? 'ring-red-400' : 'ring-transparent'}`}>
            <span className="text-[12px] font-semibold text-[#4C444E] uppercase">TO</span>
            <DatePicker
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); validateDates(fromDate, e.target.value); }}
              placeholder="dd/mm/yyyy"
              className="w-[110px]"
            />
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-3 py-2 px-4 rounded-[12px] bg-white min-w-[170px]">
            <span className="text-[12px] font-semibold text-[#4C444E] uppercase">TYPE</span>
            <Select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="min-w-[110px]"
              options={[
                { label: "All", value: "" },
                { label: "Credit", value: "credit" },
                { label: "Debit", value: "debit" },
                { label: "Hold", value: "hold" },
                { label: "Unhold", value: "unhold" },
              ]}
            />
          </div>
        </div>
        {dateError && <p className="text-[11px] text-red-500 font-medium mt-2">{dateError}</p>}
      </div>

      {/* Statement Card */}
      <div className="bg-[#FFFFFF] shadow-[0px_32px_48px_0px_#4B23640F] rounded-xl overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:justify-between p-4 sm:p-6 items-start sm:items-center gap-4 border-b border-gray-100">
          <div>
            <p className="text-[18px] md:text-[20px] font-semibold text-primary mb-0.5">Wallet Statement</p>
            <p className="text-[13px] md:text-[14px] text-[#6B6471]">Real-time ledger of your financial activities</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              className="bg-[#F3F4F5] rounded-[10px] px-3 py-2 text-[13px] text-[#191C1D] border-none outline-none cursor-pointer"
            >
              <option value="10">10 / page</option>
              <option value="25">25 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
            </select>
            {/* Export Split Button */}
            <div className="relative" ref={menuRef}>
              <div className={`flex items-center rounded-lg overflow-hidden ${exporting ? 'opacity-60 pointer-events-none' : ''}`}>
                <button
                  onClick={() => handleExport('csv')}
                  disabled={exporting}
                  className="flex items-center gap-2 bg-[#4B2364] px-4 py-[9px] text-[13px] font-semibold text-white cursor-pointer border-none transition-colors"
                >
                  {exporting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  )}
                  {exporting ? 'Exporting...' : 'Export'}
                </button>
                <button
                  onClick={() => setShowFormatMenu((v) => !v)}
                  disabled={exporting}
                  className="flex items-center justify-center bg-[#4B2364] px-2.5 h-[37px] text-white border-none cursor-pointer transition-colors"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={`transition-transform duration-200 ${showFormatMenu ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </div>

              {showFormatMenu && (
                <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden w-[160px]">
                  <div className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                    Choose Format
                  </div>
                  <button
                    onClick={() => { handleExport('csv'); setShowFormatMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors"
                  >
                    <span className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="8" y1="13" x2="16" y2="13" />
                        <line x1="8" y1="17" x2="16" y2="17" />
                      </svg>
                    </span>
                    CSV
                  </button>
                  <button
                    onClick={() => { handleExport('excel'); setShowFormatMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors"
                  >
                    <span className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14,2 14,8 20,8" />
                        <rect x="8" y="12" width="8" height="6" rx="1" />
                      </svg>
                    </span>
                    Excel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {loadingTxn && transactions.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {loadingTxn && transactions.length > 0 && (
              <div className="px-6 pt-3 text-xs text-gray-400">Refreshing data...</div>
            )}
            <div className="overflow-x-auto">
              <DataTable columns={columns} data={transactions} />
            </div>
            {transactions.length === 0 && !loadingTxn && (
              <div className="text-center py-10 text-gray-400 text-sm">No transactions found.</div>
            )}
          </>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-4 border-t border-gray-100 gap-3">
            <span className="text-xs text-[#4C444E]">
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} records
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${page === 1 ? 'bg-gray-50 text-gray-300 cursor-not-allowed' : 'bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer'}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <span className="text-xs text-[#4C444E]">
                Page {pagination.current_page} of {pagination.last_page}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!pagination.has_more}
                className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${!pagination.has_more ? 'bg-gray-50 text-gray-300 cursor-not-allowed' : 'bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer'}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}